/* eslint-disable react/no-unused-prop-types */
// this is used because all the links are actually used
// inside a function and to keep the naming somewhere it's stll saved here
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { MapTo, Utils } from '@adobe/aem-react-editable-components';
import { useMutation } from '@apollo/client';
import {
  UPDATE_EXPERT_CONSENT_MUTATION,
  UPDATE_INSTALLER_CONSENT_MUTATION,
} from 'Queries/Mutations';
import {
  createPatchEinwilligungByInstallerIdData,
  mapNetworkOperatorsToPflegendeBU,
} from 'Queries/QueriesHelper';
import { ErrorHandlingContext } from 'context/ErrorHandling';
import { useCurrentExperts } from 'hooks/UseCurrentExperts';
import { useCurrentInstaller } from 'hooks/UseCurrentInstaller';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { CustomModal } from 'components/CustomModal';
import {
  ExpandedConsentSections,
  getDefaultOperatorSelections,
} from 'components/DataPolicy/ExpandedConsentSections';
import { ExpertDataPolicyTables } from 'components/DataPolicy/ExpertDataPolicyTables';
import { DataPolicyPopup } from 'components/DataPolicyPopup/DataPolicyPopup';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { RenderAemComponent } from 'components/RenderAemComponent/RenderAemComponent';

import { PFLEGENDE_BU_TO_NETWORK_OPERATOR } from 'global/constants';

import { parseAEMDataPolicySHNGProps, parseAEMDatapolicyProps } from 'utils/AEMPropUtils';
import { getTenantId } from 'utils/tenantUtil';

import { DEFAULT_CONFIRMATION_TEXT, DataPolicyContent } from './DataPolicyContentExpanded';

import './DataPolicy.sass';

export const DataPolicy = (props) => {
  const { setShowError } = useContext(ErrorHandlingContext);
  const { isLoading: isInstallerLoading, installer, refetchInstaller } = useCurrentInstaller();
  const { experts, isLoading: isExpertsLoading, refetchExperts } = useCurrentExperts();
  const [dsgvoAddressPublic, setDsgvoAddressPublic] = useState(false);
  const [dsgvoCanSendMail, setDsgvoCanSendMail] = useState(false);
  const [informationOperators, setInformationOperators] = useState(() =>
    getDefaultOperatorSelections(getTenantId())
  );
  const [addressOperators, setAddressOperators] = useState(() =>
    getDefaultOperatorSelections(getTenantId())
  );
  const [openSubmitPopup, setOpenSubmitPopup] = useState(false);
  const [openCompanyPopup, setOpenCompanyPopup] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [expertInformationConsent, setExpertInformationConsent] = useState(false);
  const [expertInformationOperators, setExpertInformationOperators] = useState([]);
  const [updateInstallerConsentMutation, { loading: isInstallerConsentSaving }] = useMutation(
    UPDATE_INSTALLER_CONSENT_MUTATION
  );
  const [updateExpertConsentMutation, { loading: isExpertConsentSaving }] = useMutation(
    UPDATE_EXPERT_CONSENT_MUTATION
  );
  const isEditor = Utils.isInEditor();
  const normalizeOperators = (operators) =>
    (operators || []).map((operator) =>
      (PFLEGENDE_BU_TO_NETWORK_OPERATOR[operator] || operator).trim()
    );
  const sameOperators = (current, saved) =>
    JSON.stringify(normalizeOperators(current).sort()) ===
    JSON.stringify(normalizeOperators(saved).sort());
  useEffect(() => {
    if (isEditor) return;

    if (!isInstallerLoading && installer) {
      setDsgvoAddressPublic(installer.zuAdressveroeff === 'Ja');
      setDsgvoCanSendMail(installer.zuInfoversand === 'Ja');
      setInformationOperators(normalizeOperators(installer.informationOperators));
      setAddressOperators(normalizeOperators(installer.addressOperators));
    }
  }, [installer, isEditor, isInstallerLoading]);

  const submitButtonDisabled = useMemo(() => {
    const hasChanges =
      dsgvoAddressPublic !== (installer?.zuAdressveroeff === 'Ja') ||
      dsgvoCanSendMail !== (installer?.zuInfoversand === 'Ja') ||
      !sameOperators(informationOperators, installer?.informationOperators) ||
      !sameOperators(addressOperators, installer?.addressOperators);

    return openSubmitPopup || isInstallerLoading || !hasChanges;
  }, [
    isInstallerLoading,
    openSubmitPopup,
    dsgvoAddressPublic,
    dsgvoCanSendMail,
    informationOperators,
    addressOperators,
    installer,
  ]);

  const expertSubmitButtonDisabled = useMemo(() => {
    if (!selectedExpert) return true;

    return (
      expertInformationConsent === (selectedExpert.consentInfoversand === 'Ja') &&
      sameOperators(expertInformationOperators, selectedExpert.informationOperators)
    );
  }, [expertInformationConsent, expertInformationOperators, selectedExpert]);

  if (isEditor) {
    return <RenderAemComponent componentProps={props} name="DataPolicy" />;
  }

  const onSubmitValues = async () => {
    try {
      const responseData = await updateInstallerConsentMutation({
        variables: createPatchEinwilligungByInstallerIdData(
          dsgvoAddressPublic,
          dsgvoCanSendMail,
          informationOperators,
          addressOperators
        ),
      });

      const response = responseData.data?.updateInstallerConsent;
      if (responseData.error || !response || response.status !== 200) {
        throw new Error(
          responseData.error?.message || `Mutation error with status: ${response?.status}`
        );
      }

      setDsgvoAddressPublic(response.zuAdressveroeff === 'Ja');
      setDsgvoCanSendMail(response.zuInfoversand === 'Ja');
      setOpenCompanyPopup(false);
      setOpenSubmitPopup(true);
      await refetchInstaller();
    } catch (e) {
      console.error(e);
      setOpenCompanyPopup(false);
      setShowError(true);
    }
  };

  const resetCompanyDraft = () => {
    setDsgvoAddressPublic(installer?.zuAdressveroeff === 'Ja');
    setDsgvoCanSendMail(installer?.zuInfoversand === 'Ja');
    setInformationOperators(normalizeOperators(installer?.informationOperators));
    setAddressOperators(normalizeOperators(installer?.addressOperators));
    setOpenCompanyPopup(false);
  };

  const onExpertEdit = (expert) => {
    setSelectedExpert(expert);
    setExpertInformationConsent(expert.consentInfoversand === 'Ja');
    setExpertInformationOperators(normalizeOperators(expert.informationOperators));
  };

  const onExpertSubmit = async () => {
    if (!selectedExpert?.egcid) {
      setSelectedExpert(null);
      setShowError(true);
      return;
    }

    try {
      const responseData = await updateExpertConsentMutation({
        variables: {
          expertEgcid: selectedExpert.egcid,
          input: {
            zuInfoversand: expertInformationConsent ? 'Ja' : 'Nein',
            informationOperators: mapNetworkOperatorsToPflegendeBU(expertInformationOperators),
          },
        },
      });
      const response = responseData.data?.updateExpertConsent;
      if (responseData.error || !response || response.status !== 200) {
        throw new Error(
          responseData.error?.message || `Mutation error with status: ${response?.status}`
        );
      }
      setSelectedExpert(null);
      setOpenSubmitPopup(true);
      await refetchExperts();
    } catch (e) {
      console.error(e);
      setSelectedExpert(null);
      setShowError(true);
    }
  };

  return (
    <div className="data-policy">
      {!installer || isInstallerLoading || isExpertsLoading || !experts ? (
        <LoadingIndicator />
      ) : (
        <>
          <ExpertDataPolicyTables
            installer={installer}
            experts={experts}
            onCompanyEdit={() => {
              setOpenCompanyPopup(true);
            }}
            onExpertEdit={onExpertEdit}
          />
          <DataPolicyContent
            dsgvoAddressPublic={dsgvoAddressPublic}
            dsgvoCanSendMail={dsgvoCanSendMail}
            dsgvoAddressPublicName="zuAdressveroeff"
            dsgvoCanSendMailName="zuInfoversand"
            onChangeDsvgoAddressPublic={() => undefined}
            onChangeDsvgoCanSendMail={() => undefined}
            documents={parseAEMDatapolicyProps(props)}
            shngDocuments={parseAEMDataPolicySHNGProps(props)}
            showConsentSection={false}
          />
          <CustomModal
            isOpen={openCompanyPopup}
            title="Datenschutz bearbeiten"
            closeCancelEventModal={resetCompanyDraft}
            content={
              <ExpandedConsentSections
                currentBusinessUnit={installer.pflegendeBu}
                values={{
                  information: dsgvoCanSendMail,
                  address: dsgvoAddressPublic,
                  informationOperators,
                  addressOperators,
                }}
                onChange={({
                  information,
                  address,
                  informationOperators: nextInformation,
                  addressOperators: nextAddress,
                }) => {
                  setDsgvoCanSendMail(information);
                  setDsgvoAddressPublic(address);
                  setInformationOperators(nextInformation);
                  setAddressOperators(nextAddress);
                }}
              />
            }
            ctaButtons={
              <>
                <ActionButton title="Abbrechen" inverted onClick={resetCompanyDraft} />
                <ActionButton
                  title="Datenschutz Änderungen bestätigen"
                  disabled={submitButtonDisabled || isInstallerConsentSaving}
                  loading={isInstallerConsentSaving}
                  onClick={onSubmitValues}
                />
              </>
            }
          />
          <CustomModal
            isOpen={selectedExpert !== null}
            title="Datenschutz bearbeiten"
            closeCancelEventModal={() => setSelectedExpert(null)}
            content={
              <div>
                <p>
                  Für die Fachkraft{' '}
                  <strong>
                    {selectedExpert?.vorname} {selectedExpert?.nachname}
                  </strong>
                </p>
                <ExpandedConsentSections
                  showAddress={false}
                  boxed={false}
                  currentBusinessUnit={installer.pflegendeBu}
                  values={{
                    information: expertInformationConsent,
                    address: false,
                    informationOperators: expertInformationOperators,
                    addressOperators: [],
                  }}
                  onChange={({ information, informationOperators: nextInformation }) => {
                    setExpertInformationConsent(information);
                    setExpertInformationOperators(nextInformation);
                  }}
                />
                <div className="data-policy__confirmation-text">{DEFAULT_CONFIRMATION_TEXT}</div>
              </div>
            }
            ctaButtons={
              <>
                <ActionButton title="Abbrechen" inverted onClick={() => setSelectedExpert(null)} />
                <ActionButton
                  title="Datenschutz Änderungen bestätigen"
                  disabled={expertSubmitButtonDisabled || isExpertConsentSaving}
                  loading={isExpertConsentSaving}
                  onClick={onExpertSubmit}
                />
              </>
            }
          />
          <DataPolicyPopup
            openSubmitPopup={openSubmitPopup}
            setOpenSubmitPopup={setOpenSubmitPopup}
          />
        </>
      )}
    </div>
  );
};

// Look on top for eslint rule explanation

DataPolicy.propTypes = {
  datapolicyEDILink: PropTypes.string,
  datapolicyAVALink: PropTypes.string,
  datapolicySHNGLink: PropTypes.string,
  datapolicyBAGLink: PropTypes.string,
  datapolicyEENGLink: PropTypes.string,
  datapolicyHANSGLink: PropTypes.string,
  datapolicyNNNGLink: PropTypes.string,
  datapolicyMTNLink: PropTypes.string,
  datapolicyLVNLink: PropTypes.string,
  datapolicyEDILabel: PropTypes.string,
  datapolicyAVALabel: PropTypes.string,
  datapolicySHNGLabel: PropTypes.string,
  datapolicyBAGLabel: PropTypes.string,
  datapolicyEENGLabel: PropTypes.string,
  datapolicyHANSGLabel: PropTypes.string,
  datapolicyNNNGLabel: PropTypes.string,
  datapolicyMTNLabel: PropTypes.string,
  datapolicyLVNLabel: PropTypes.string,
};

DataPolicy.defaultProps = {
  datapolicyEDILink: '',
  datapolicyAVALink: '',
  datapolicySHNGLink: '',
  datapolicyBAGLink: '',
  datapolicyEENGLink: '',
  datapolicyHANSGLink: '',
  datapolicyNNNGLink: '',
  datapolicyMTNLink: '',
  datapolicyLVNLink: '',
  datapolicyEDILabel: '',
  datapolicyAVALabel: '',
  datapolicySHNGLabel: '',
  datapolicyBAGLabel: '',
  datapolicyEENGLabel: '',
  datapolicyHANSGLabel: '',
  datapolicyNNNGLabel: '',
  datapolicyMTNLabel: '',
  datapolicyLVNLabel: '',
};

MapTo(['e-fix/components/content/datapolicy'])(DataPolicy);
